import { usePracticeScope } from '../../../../onboarding/practice/practiceScope';
import { PRACTICE_DISH_ID } from '../../../../onboarding/practice/practiceGuards';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeftRight, ExternalLink, Link2, Pencil, Trash2 } from 'lucide-react';
import {
  UtilizationProductRow,
  UtilizationGroupMember,
  DishNeedBreakdown,
  RecentProductOrder,
  getUtilizationStatus,
  poolShareOf,
} from '../../../../../utils/reports/utilizationReport';
import {
  WasteByProductRow,
  WasteContribution,
  WASTE_REASON_LABELS,
  formatWasteQuantity,
} from '../../../../../utils/reports/wasteByProduct';
import { Platform } from '../../../../../types/Platforms';
import { SupplierLogo } from '../../../../ui/SupplierLogo';
import { SlideOverShell, SectionTitle, OverviewCard, BreakdownCard } from '../../../../ui/slideOverParts';
import { eur } from '../../../../orderhistory/utils';
import { fmtBase } from '../../../../../utils/reports/shared';
import { prepPathLines } from '../../../../../utils/reports/prepPathLabel';
import { getTableFormattedDate } from '../../../../../utils/dateUtils';
import { getCategoryStyles } from '../../../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../../../orderhistory/sections/FilterControls';
import { cn } from '../../../../../lib/utils';
import { pctAbs } from '../common';
import { useAppSettingsStore } from '../../../../../stores/appSettingsStore';

const STATUS_BAR: Record<ReturnType<typeof getUtilizationStatus>, string> = {
  healthy: '#12B76A',
  review: '#F79009',
  critical: '#F04438',
};

/** Stable empty fallbacks so `?? []` doesn't create a new array every render (useMemo deps). */
const EMPTY_DISH_BREAKDOWN: DishNeedBreakdown[] = [];
const EMPTY_RECENT_ORDERS: RecentProductOrder[] = [];

/** Per-portion amounts are small, so they read better rounded than via `fmtBase`. Below
 *  10 they keep a decimal: a per-plate g/ml amount can legitimately be under 1 (a spice,
 *  a splash from a prep), and rounding those to a bare "0g" reads as a bug. */
function fmtPerPortion(value: number, stdUnit: string): string {
  const round = (v: number) => (v < 10 ? Math.round(v * 10) / 10 : Math.round(v));
  if (stdUnit === 'g') return `${round(value)}g`;
  if (stdUnit === 'ml') return `${round(value)}ml`;
  if (stdUnit === 'p') return `${Math.round(value * 100) / 100} pcs`;
  return fmtBase(value, stdUnit);
}

/** Latest price per base unit rendered in shop units: €/kg, €/L or €/pcs. */
function unitPriceLabel(pricePerBase: number, stdUnit: string): string {
  if (stdUnit === 'g') return `${eur(pricePerBase * 1000)}/kg`;
  if (stdUnit === 'ml') return `${eur(pricePerBase * 1000)}/L`;
  if (stdUnit === 'p') return `${eur(pricePerBase)}/pcs`;
  return eur(pricePerBase);
}

/** Entries shown before the list collapses into a "+N more" line — the drawer is a
 *  summary; the waste page is the full ledger. */
const REPORTED_WASTE_PREVIEW = 10;

interface UsedDishesDrawerProps {
  product: UtilizationProductRow | null;
  onClose: () => void;
  /** Report period, shown next to the alternatives count in the header meta line. */
  periodLabel?: string;
  /** Pool / group members when this product belongs to an alternative set. */
  groupMembers?: UtilizationGroupMember[];
  /** Open the cost-price editor for one of the breakdown's dishes. */
  onEditDish?: (costPriceId: string) => void;
  /** Start the link flow (the not-linked table's Setup flow) for this product. */
  onAddLink?: () => void;
  /** Waste staff actually LOGGED against this product in the period.
   *  `undefined` = the account has no waste page (section is not rendered at all);
   *  `null` = it does, but nothing was logged for this product (empty state). */
  reportedWaste?: WasteByProductRow | null;
  /** A panel the host stacked on top of this drawer is open — see SlideOverShell. */
  overlayOpen?: boolean;
  notice?: React.ReactNode;
  /** Organization overview: restaurant drilldowns follow the combined metrics. */
  afterOverview?: React.ReactNode;
}

/**
 * Right-side drawer explaining one product: the ordered-vs-expected overview, HOW the
 * needed quantity was derived (per-dish `per portion × dishes sold = needed` cards),
 * its interchangeable alternatives, the dishes linked to it and its recent orders.
 */
export const UsedDishesDrawer: React.FC<UsedDishesDrawerProps> = ({
  product,
  onClose,
  periodLabel,
  groupMembers = [],
  onEditDish,
  onAddLink,
  reportedWaste,
  overlayOpen = false,
  notice,
  afterOverview,
}) => {
  const { t } = useTranslation();
  const practice = usePracticeScope();
  const darkMode = useAppSettingsStore((s) => s.darkMode);

  // Dishes whose demand reached this product via alternative-allocation (drawer marker).
  const viaAlternative = useMemo(
    () => new Set(product?.dishesViaAlternative ?? []),
    [product]
  );

  const dishBreakdown = product?.dishBreakdown ?? EMPTY_DISH_BREAKDOWN;
  const recentOrders = product?.recentOrders ?? EMPTY_RECENT_ORDERS;
  const alternativeForNames = product?.alternativeForNames ?? [];

  const metrics = useMemo(() => {
    if (!product) return null;
    const price = product.pricePerBase;
    const locals = product.restaurantContributions;
    const allLocalsPriced = locals?.every(row => row.pricePerBase !== null);
    const orderedVal = locals
      ? allLocalsPriced ? locals.reduce((sum, row) => sum + row.orderedBase * row.pricePerBase!, 0) : null
      : price !== null ? product.orderedBase * price : null;
    const neededVal = locals
      ? allLocalsPriced ? locals.reduce((sum, row) => sum + row.neededBase * row.pricePerBase!, 0) : null
      : price !== null ? product.neededBase * price : null;
    return {
      orderedVal,
      neededVal,
      totalSpend: product.spend,
      variedPrices: !!allLocalsPriced && price === null,
      // `portions` is now real units sold per dish — summable as-is (rounded only for
      // display, since a weighed menu item can carry a fractional quantity).
      totalDishesSold: Math.round(dishBreakdown.reduce((s, d) => s + d.portions, 0)),
      totalExpectedUsage: dishBreakdown.reduce((s, d) => s + d.neededBase, 0),
    };
  }, [product, dishBreakdown]);

  const alternativeProducts = useMemo(() => {
    if (!product || groupMembers.length <= 1) return [];
    return groupMembers.filter((m) => m.key !== product.key);
  }, [product, groupMembers]);

  if (!product) return null;

  const platform = Platform.fromString(product.platform);
  const utilStatus = getUtilizationStatus(product.utilizationPercent);
  const utilPct = product.utilizationPercent ?? 0;
  const showAlternativesMeta = groupMembers.length > 1 || alternativeForNames.length > 0;
  const poolShare = poolShareOf(product.neededBase, metrics?.totalExpectedUsage ?? 0);

  const headerMeta = [
    showAlternativesMeta
      ? t('reports.utilization.drawer.alternativesMeta', {
          count: Math.max(groupMembers.length - 1, alternativeForNames.length),
        })
      : null,
    periodLabel,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <SlideOverShell onClose={onClose} overlayOpen={overlayOpen} onboardingId={practice ? "utilization-drawer" : undefined}>
      {notice}
      {headerMeta && (
        <p className="text-sm font-medium text-[#0E8965] dark:text-darkGreenColor-dark mb-3">{headerMeta}</p>
      )}

      <h2 className="text-2xl sm:text-[28px] leading-8 sm:leading-[39px] font-bold tracking-[-0.1px] text-textPrimary dark:text-textPrimary-dark">
        {product.name}
      </h2>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px] font-medium text-muted dark:text-muted-dark">
        {product.category && (
          <>
            <span className="text-xs font-medium rounded-md px-2 py-1 whitespace-nowrap" style={getCategoryStyles(product.category, darkMode)}>
              {formatCategoryName(product.category)}
            </span>
            <span aria-hidden>•</span>
          </>
        )}
        <span className="inline-flex items-center gap-1.5">
          <SupplierLogo
            platform={platform}
            unknownSupplierName={product.unknownSupplierName}
            size="small"
            alt={product.supplier}
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
          {product.supplier}
        </span>
        {product.articleId && (
          <>
            <span aria-hidden>•</span>
            <span>{product.articleId}</span>
          </>
        )}
      </div>

      {product.receivedViaAlternative && alternativeForNames.length > 0 && (
        <p className="mt-3 text-sm font-medium text-blue-600 dark:text-blue-300">
          {alternativeForNames.length === 1
            ? t('reports.utilization.alternativeFor', { name: alternativeForNames[0] })
            : t('reports.utilization.alternativeForMany', { count: alternativeForNames.length })}
        </p>
      )}

      <SectionTitle className="mt-6">{t('reports.utilization.drawer.utilisationOverview')}</SectionTitle>
      <div data-onboarding={practice ? "utilization-overview" : undefined} className="mt-4 grid grid-cols-2 gap-2 sm:gap-2.5">
        <OverviewCard
          label={t('reports.utilization.col.ordered')}
          value={fmtBase(product.orderedBase, product.stdUnit)}
          sub={metrics?.orderedVal != null ? t('reports.utilization.drawer.valueEur', { amount: eur(metrics.orderedVal) }) : undefined}
        />
        <OverviewCard
          label={t('reports.utilization.drawer.expected')}
          value={fmtBase(product.neededBase, product.stdUnit)}
          sub={metrics?.neededVal != null ? t('reports.utilization.drawer.valueEur', { amount: eur(metrics.neededVal) }) : undefined}
        />
        <OverviewCard
          label={t('reports.utilization.col.difference')}
          value={`${product.wasteBase > 0 ? '+' : ''}${fmtBase(product.wasteBase, product.stdUnit)}`}
          sub={
            product.wasteBase > 0
              ? t('reports.utilization.drawer.morePurchasedThanExpected')
              : product.wasteBase < 0
              ? t('reports.utilization.drawer.lessPurchasedThanExpected')
              : undefined
          }
          tone={product.wasteBase > 0 ? 'bad' : product.wasteBase < 0 ? 'warn' : 'neutral'}
        />
        <OverviewCard
          label={t('reports.utilization.drawer.potentialWaste')}
          value={product.wasteEur !== null ? eur(product.wasteEur) : t('reports.utilization.naValue')}
          sub={t(product.restaurantContributions ? 'reports.utilization.organization.restaurantPrices' : 'reports.utilization.drawer.atLatestPrice')}
          tone={product.wasteEur !== null && product.wasteEur > 0 ? 'bad' : 'neutral'}
        />
        <OverviewCard
          label={t('reports.utilization.drawer.unitPrice')}
          value={product.pricePerBase !== null ? unitPriceLabel(product.pricePerBase, product.stdUnit) : t(metrics?.variedPrices ? 'reports.utilization.organization.variedPrices' : 'reports.utilization.naValue')}
        />
        <OverviewCard
          label={t('reports.utilization.drawer.totalSpend')}
          value={metrics?.totalSpend != null ? eur(metrics.totalSpend) : t('reports.utilization.naValue')}
        />
      </div>

      {product.utilizationPercent !== null && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
              {t('reports.utilization.col.utilization')}
            </span>
            <span
              className="inline-flex items-center rounded-lg px-2 text-sm leading-7 font-semibold"
              style={{ color: STATUS_BAR[utilStatus], backgroundColor: `${STATUS_BAR[utilStatus]}1A` }}
            >
              {pctAbs(product.utilizationPercent)}
            </span>
          </div>
          <div className="h-3 rounded-full bg-backgroundDarker dark:bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(100, utilPct)}%`, backgroundColor: STATUS_BAR[utilStatus] }}
            />
          </div>
        </div>
      )}

      {afterOverview}

      {reportedWaste !== undefined && (
        <ReportedWasteSection
          row={reportedWaste}
          stdUnit={product.stdUnit}
          wasteBase={product.wasteBase}
          pricePerBase={product.pricePerBase}
          onEditDish={onEditDish}
        />
      )}

      <div data-onboarding={practice ? "utilization-breakdown" : undefined} className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>{t('reports.utilization.drawer.neededBreakdown')}</SectionTitle>
        {/* Always available: a breakdown is only ever as complete as the cost prices behind
            it, so linking another dish must stay one click away even when some already show.
            Same flow as the not-linked table's Setup — it appends to an existing analysis
            and skips silently if this product is already on it. */}
        {onAddLink && (
          <button
            type="button"
            data-onboarding={practice ? "utilization-add-link" : undefined}
            onClick={onAddLink}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[#0E8965] dark:border-darkGreenColor-dark text-xs font-semibold text-[#0E8965] dark:text-darkGreenColor-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
          >
            <Link2 className="w-4 h-4" />
            {t('reports.utilization.drawer.addLink')}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm font-medium text-muted dark:text-muted-dark">
        {t('reports.utilization.drawer.neededBreakdownHint', { qty: fmtBase(product.neededBase, product.stdUnit) })}
      </p>

      {dishBreakdown.length === 0 ? (
        <p className="mt-4 text-sm text-muted dark:text-muted-dark">{t('reports.utilization.drawer.noDishes')}</p>
      ) : (
        <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-3 flex flex-col gap-5">
          {dishBreakdown.map((d) => (
            <DishBreakdownCard
              key={d.dishName}
              dish={d}
              stdUnit={product.stdUnit}
              viaAlternative={viaAlternative.has(d.dishName)}
              onEdit={onEditDish && d.costPriceId ? () => onEditDish(d.costPriceId as string) : undefined}
            />
          ))}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 pt-3 border-t border-dashed border-border dark:border-border-dark">
            <div className="text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
              {t('reports.utilization.drawer.totalDishSold')}:{' '}
              <span className="text-lg sm:text-xl font-semibold text-textPrimary dark:text-textPrimary-dark">
                {metrics?.totalDishesSold ?? 0}
              </span>
            </div>
            <div className="sm:text-right text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
              {t('reports.utilization.drawer.totalExpectedUsage')}:{' '}
              <span className="text-lg sm:text-xl font-semibold text-textPrimary dark:text-textPrimary-dark">
                {fmtBase(metrics?.totalExpectedUsage ?? 0, product.stdUnit)}
              </span>
            </div>
          </div>
          {poolShare !== null && (
            <div className="flex items-start gap-1.5 pt-3 border-t border-dashed border-border dark:border-border-dark text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
              <ArrowLeftRight className="w-4 h-4 shrink-0 mt-0.5 sm:mt-1 text-blue-600 dark:text-blue-300" aria-hidden />
              <span>
                <Trans
                  t={t}
                  i18nKey="reports.utilization.drawer.poolShare"
                  values={{
                    qty: fmtBase(product.neededBase, product.stdUnit),
                    total: fmtBase(metrics?.totalExpectedUsage ?? 0, product.stdUnit),
                    pct: pctAbs(poolShare * 100, 0),
                  }}
                  components={{ figure: <span className="font-semibold text-textPrimary dark:text-textPrimary-dark" /> }}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {alternativeProducts.length > 0 && (
        <>
          <SectionTitle className="mt-6">{t('reports.utilization.drawer.alternativeProducts')}</SectionTitle>
          <ul className="mt-4 flex flex-col gap-4">
            {alternativeProducts.map((m) => (
              <AlternativeProductCard key={m.key} member={m} fallbackPricePerBase={product.pricePerBase} />
            ))}
          </ul>
        </>
      )}

      {product.subrecipes.length > 0 && (
        <>
          <SectionTitle className="mt-6">{t('reports.utilization.drawer.subrecipes')}</SectionTitle>
          <ul className="mt-3 flex flex-wrap gap-2">
            {product.subrecipes.map((s) => (
              <li key={s} className="text-xs text-muted dark:text-muted-dark border border-border dark:border-border-dark rounded-full px-2.5 py-1">
                {s}
              </li>
            ))}
          </ul>
        </>
      )}

      {recentOrders.length > 0 && (
        <>
          <SectionTitle className="mt-6">{t('reports.utilization.drawer.recentOrders')}</SectionTitle>
          <ul className="mt-3 flex flex-col gap-2">
            {recentOrders.map((o) => (
              <li
                key={o.orderHistoryId}
                className="flex items-center justify-between gap-2 text-sm bg-backgroundDarker dark:bg-backgroundDarker-dark border border-border dark:border-border-dark rounded-xl px-3 py-2.5"
              >
                <span className="text-textPrimary dark:text-textPrimary-dark whitespace-nowrap">
                  {getTableFormattedDate(new Date(o.orderDate))}
                </span>
                <span className="text-muted dark:text-muted-dark truncate">
                  {o.quantity} × {eur(o.unitPrice)}
                </span>
                <a
                  href={`/orderhistory/${o.orderHistoryId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[13px] font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                >
                  {t('reports.utilization.drawer.view')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </SlideOverShell>
  );
};

/** One dish's `per portion × dishes sold = needed` formula row. */
const DishBreakdownCard: React.FC<{
  dish: DishNeedBreakdown;
  stdUnit: string;
  viaAlternative: boolean;
  onEdit?: () => void;
}> = ({ dish, stdUnit, viaAlternative, onEdit }) => {
  const { t } = useTranslation();
  const practice = usePracticeScope();

  return (
    <BreakdownCard
      onboardingId={practice && dish.costPriceId === PRACTICE_DISH_ID ? "utilization-edit-dish" : undefined}
      title={dish.dishName}
      titleBadge={
        viaAlternative ? (
          <span title={t('reports.utilization.drawer.viaAlternative')} className="shrink-0">
            <ArrowLeftRight className="w-4 h-4 text-blue-600 dark:text-blue-300" aria-hidden />
          </span>
        ) : undefined
      }
      value={dish.valueEur !== null ? eur(dish.valueEur) : undefined}
      onEdit={onEdit}
      // Where `perPortionBase` came from, when it came through a prep. Empty for a dish
      // that lists the product directly, which is what keeps those cards unchanged.
      notes={prepPathLines(dish.prepPaths, (v) => fmtPerPortion(v, stdUnit), t)}
      perUnit={fmtPerPortion(dish.perPortionBase, stdUnit)}
      perUnitSub={t('reports.utilization.drawer.perDish')}
      units={String(Math.round(dish.portions))}
      unitsSub={t('reports.utilization.drawer.dishesSold')}
      total={fmtBase(dish.neededBase, stdUnit)}
    />
  );
};

/**
 * "Reported waste": what staff actually LOGGED against this product in the period —
 * dish entries exploded through their recipe plus directly-logged ingredient entries
 * (`computeWasteByProduct`). Purely explanatory: it sits next to the potential-waste
 * number above but changes none of it.
 */
const ReportedWasteSection: React.FC<{
  row: WasteByProductRow | null;
  stdUnit: string;
  wasteBase: number;
  pricePerBase: number | null;
  onEditDish?: (costPriceId: string) => void;
}> = ({ row, stdUnit, wasteBase, pricePerBase, onEditDish }) => {
  const { t } = useTranslation();

  const contributions = row?.contributions ?? [];

  if (!row || contributions.length === 0) {
    return (
      <>
        <SectionTitle className="mt-6">{t('reports.utilization.drawer.reportedWaste.title')}</SectionTitle>
        <p className="mt-4 text-sm text-muted dark:text-muted-dark">
          {t('reports.utilization.drawer.reportedWaste.empty')}
        </p>
      </>
    );
  }

  const unit = row.stdUnit || stdUnit;
  const valueEur = pricePerBase !== null ? row.totalQuantity * pricePerBase : null;
  // Both sides are base units in the same std unit, so the share is a like-for-like ratio.
  // Capped: more can be thrown away than was over-ordered (the rest came out of stock).
  const sharePct = wasteBase > 0 && row.totalQuantity > 0 ? Math.min(100, (row.totalQuantity / wasteBase) * 100) : null;
  const preview = contributions.slice(0, REPORTED_WASTE_PREVIEW);
  const hiddenCount = contributions.length - preview.length;

  return (
    <>
      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-2">
        <SectionTitle>{t('reports.utilization.drawer.reportedWaste.title')}</SectionTitle>
        <span className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
          {fmtBase(row.totalQuantity, unit)}
          {valueEur !== null && (
            <span className="ml-2 text-sm font-medium text-muted dark:text-muted-dark">{eur(valueEur)}</span>
          )}
        </span>
      </div>
      <p className="mt-1 text-sm font-medium text-muted dark:text-muted-dark">
        {sharePct !== null
          ? t('reports.utilization.drawer.reportedWaste.explainsGap', {
              qty: fmtBase(row.totalQuantity, unit),
              gap: fmtBase(wasteBase, unit),
              pct: pctAbs(sharePct),
            })
          : t('reports.utilization.drawer.reportedWaste.hint')}
      </p>

      <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-3 flex flex-col gap-3">
        {preview.map((c) => (
          <ReportedWasteCard
            key={`${c.wasteId}-${c.source}`}
            contribution={c}
            stdUnit={unit}
            onEdit={onEditDish && c.costPriceId ? () => onEditDish(c.costPriceId as string) : undefined}
          />
        ))}

        {hiddenCount > 0 && (
          <p className="text-center text-[13px] font-medium text-muted dark:text-muted-dark">
            {t('reports.utilization.drawer.reportedWaste.more', { count: hiddenCount })}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 pt-3 border-t border-dashed border-border dark:border-border-dark text-sm font-medium text-muted dark:text-muted-dark">
          <span>
            {t('reports.utilization.drawer.reportedWaste.fromDishes', {
              qty: fmtBase(row.fromDishQuantity, unit),
              count: row.fromDishEntries,
            })}
          </span>
          <span className="sm:text-right">
            {t('reports.utilization.drawer.reportedWaste.fromIngredients', {
              qty: fmtBase(row.fromIngredientQuantity, unit),
              count: row.fromIngredientEntries,
            })}
          </span>
        </div>
      </div>
    </>
  );
};

/** One logged waste entry's share of this product. */
const ReportedWasteCard: React.FC<{
  contribution: WasteContribution;
  stdUnit: string;
  onEdit?: () => void;
}> = ({ contribution: c, stdUnit, onEdit }) => {
  const { t } = useTranslation();

  const mismatched = c.quantity === null;

  return (
    <div
      className={cn(
        'rounded-xl p-3 sm:p-4 min-w-0',
        mismatched
          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
          : 'bg-background dark:bg-background-dark'
      )}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <p className="text-sm sm:text-base font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
            {c.itemName}
          </p>
          <p className="mt-0.5 text-[12px] font-medium text-muted dark:text-muted-dark">
            {getTableFormattedDate(new Date(c.wasteDate))}
            {c.source === 'dish' && (
              <>
                {' • '}
                {t('reports.utilization.drawer.reportedWaste.fromDish', {
                  count: c.loggedQuantity,
                  dish: c.dishName ?? c.itemName,
                })}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {mismatched ? (
            <span className="text-[13px] font-semibold text-amber-800 dark:text-amber-200">
              {formatWasteQuantity(c.loggedQuantity, c.loggedUnit)}
            </span>
          ) : (
            <span className="text-base font-bold text-textPrimary dark:text-textPrimary-dark">
              {fmtBase(c.quantity as number, stdUnit)}
            </span>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 h-8 px-2 rounded-lg border border-border dark:border-border-dark text-xs font-medium text-textSecondary dark:text-textSecondary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted dark:text-muted-dark border border-border dark:border-border-dark rounded-full px-2.5 py-1">
          <Trash2 className="w-3 h-3" aria-hidden />
          {c.reason ? t(WASTE_REASON_LABELS[c.reason] || c.reason) : t('waste.byProduct.reasonUnset')}
        </span>
        {c.reasonNote && (
          <span className="text-xs text-muted dark:text-muted-dark truncate">{c.reasonNote}</span>
        )}
      </div>

      {mismatched && (
        <p className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden />
          {t('reports.utilization.drawer.reportedWaste.unitMismatch', {
            entryUnit: c.loggedUnit,
            productUnit: stdUnit,
          })}
        </p>
      )}
    </div>
  );
};

/** An interchangeable product in the same alternative pool. */
const AlternativeProductCard: React.FC<{ member: UtilizationGroupMember; fallbackPricePerBase: number | null }> = ({
  member,
  fallbackPricePerBase,
}) => {
  const { t } = useTranslation();

  const mPlatform = Platform.fromString(member.platform);
  const pricePerBase = member.row?.pricePerBase ?? fallbackPricePerBase;
  const showsWaste = member.wasteEur !== null && member.wasteEur > 0;
  const displayPrice =
    showsWaste
      ? member.wasteEur
      : pricePerBase !== null
      ? member.orderedBase * pricePerBase
      : null;

  return (
    <li className="rounded-xl p-3 sm:p-4 border border-border dark:border-border-dark bg-background dark:bg-background-dark overflow-hidden min-w-0">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <p className="min-w-0 truncate text-base sm:text-xl font-bold text-textPrimary dark:text-textPrimary-dark">{member.name}</p>
        {displayPrice !== null && (
          <div className="shrink-0 text-right">
            <span className="text-base sm:text-lg font-bold text-[#2E90FA] dark:text-blue-400">{eur(displayPrice)}</span>
            {showsWaste && (
              <p className="text-xs sm:text-sm text-muted dark:text-muted-dark">{t('reports.utilization.drawer.potentialWaste')}</p>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 mt-3 sm:mt-4 pt-3 border-t border-border dark:border-border-dark">
        <div className="pr-2 border-r border-border dark:border-border-dark min-w-0">
          <p className="text-xs sm:text-sm text-muted dark:text-muted-dark">{t('reports.utilization.col.ordered')}</p>
          <p className="mt-1.5 text-sm sm:text-base font-semibold text-textPrimary dark:text-textPrimary-dark break-words">
            {fmtBase(member.orderedBase, member.stdUnit)}
          </p>
        </div>
        <div className="pl-5 pr-2 sm:border-r border-border dark:border-border-dark min-w-0">
          <p className="text-xs sm:text-sm text-muted dark:text-muted-dark">{t('reports.utilization.drawer.expected')}</p>
          <p className="mt-1.5 text-sm sm:text-base font-semibold text-textPrimary dark:text-textPrimary-dark break-words">
            {fmtBase(member.neededBase, member.stdUnit)}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1 mt-3 pt-3 border-t sm:mt-0 sm:pt-0 sm:border-t-0 sm:pl-5 border-border dark:border-border-dark min-w-0">
          <p className="text-xs sm:text-sm text-muted dark:text-muted-dark">{t('reports.utilization.drawer.supplier')}</p>
          <div className="mt-1.5 inline-flex items-center gap-1.5 max-w-full min-w-0">
            <SupplierLogo
              platform={mPlatform}
              unknownSupplierName={member.unknownSupplierName}
              size="small"
              alt={member.supplier}
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
            <span className="text-sm sm:text-base text-textPrimary dark:text-textPrimary-dark truncate">{member.supplier}</span>
          </div>
        </div>
      </div>
    </li>
  );
};

export default UsedDishesDrawer;
